import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLinkIcon } from '@radix-ui/react-icons'

import { userCancelTransaction } from '@/requests/order'
import { PaytrieAPI } from '@paytrie/client'
import { formatMoney } from '@paytrie/common/utils'
import { StatusBadge } from '@/components/StatusBadge'
import {
  formatCurrency,
  formatDate,
  getConfirmationUrl,
  getNetwork,
} from './dashboardDisplayUtils'
import { truncateMiddle } from '@/utils/string'
import {
  CANCELLABLE_STATUSES,
  getStatusText,
  getStatusTone,
} from './transactionStatus'

type TransactionRowProps = {
  index: number
  transactionId: string
  status: PaytrieAPI.TransactionStatus
  date: string
  wallet: string
  rightSideLabel: string
  rightSideValue: number
  leftSideLabel: string
  leftSideValue: number
  paymentId: string
  refreshTransactions: () => void
}

export const TransactionRow = ({
  index,
  transactionId,
  status,
  date,
  wallet,
  rightSideLabel,
  rightSideValue,
  leftSideLabel,
  leftSideValue,
  paymentId,
  refreshTransactions,
}: TransactionRowProps) => {
  const { t } = useTranslation()
  const [isCancelConfirmVisible, setIsCancelConfirmVisible] = useState(false)

  const handleCancelTransaction = async () => {
    try {
      const response = await userCancelTransaction(transactionId)
      if (response.status === 200) {
        refreshTransactions()
      } else {
        console.error('Error cancelling transaction. status:', response.status)
      }
    } catch (error) {
      console.error('Error cancelling transaction:', error)
    } finally {
      setIsCancelConfirmVisible(false)
    }
  }

  const showCancelButton =
    status !== null && CANCELLABLE_STATUSES.includes(status)

  return (
    <>
      <tr
        className="cursor-pointer"
        onClick={() => showTXNDetails(index)}
        title={t('transactionRows.viewDetailsTooltip')}
        data-testid={`row-transaction-${index}`}
      >
        <td
          className="cursor-pointer text-sm py-3 sm:py-6 border-b border-gray-300 dark:border-gray-700 text-left break-all"
          data-testid={`cell-transaction-${index}-date`}
        >
          {formatDate(date)}
        </td>
        <td
          className="cursor-pointer text-sm py-3 sm:py-6 border-b border-gray-300 dark:border-gray-700 px-2 break-all"
          data-testid={`cell-transaction-${index}-txid`}
        >
          {transactionId}
        </td>
        <td
          className="cursor-pointer text-sm py-3 sm:py-6 border-b border-gray-300 dark:border-gray-700 px-2 break-all"
          data-testid={`cell-transaction-${index}-from`}
        >
          {formatMoney(leftSideValue)} {formatCurrency(leftSideLabel)}
        </td>
        <td
          className="cursor-pointer text-sm py-3 sm:py-6 border-b border-gray-300 dark:border-gray-700 px-2 break-all"
          data-testid={`cell-transaction-${index}-to`}
        >
          {formatMoney(rightSideValue)} {formatCurrency(rightSideLabel)}
        </td>
        <td
          className="cursor-pointer text-sm py-3 sm:py-6 border-b border-gray-300 dark:border-gray-700 text-center"
          data-testid={`cell-transaction-${index}-status`}
        >
          <StatusBadge
            tone={getStatusTone(status)}
            testId={`indicator-transaction-${index}-status`}
          >
            {getStatusText(status)}
          </StatusBadge>
        </td>
      </tr>
      <tr
        id={`panel${index}`}
        className="hidden bg-cloudDarker"
        data-testid={`container-transaction-detail-${index}`}
      >
        <td colSpan={5} className="dark:bg-darkCharcoal py-2 sm:py-4 px-2">
          <div
            className="text-sm ml-2 p-1 sm:p-2 rounded border-gray-300"
            data-testid={`container-transaction-detail-content-${index}`}
          >
            <div
              className="flex flex-col font-bold sm:flex-row justify-between py-1"
              data-testid={`container-transaction-detail-date-${index}`}
            >
              <span className="font-bold">
                {t('transactionRows.details.date')}
              </span>{' '}
              <span className="font-normal">{formatDate(date)}</span>
            </div>
            <div
              className="flex flex-col font-bold sm:flex-row justify-between py-1"
              data-testid={`container-transaction-detail-wallet-${index}`}
            >
              <strong>{t('transactionRows.details.wallet')}</strong>
              <span className="font-normal">
                <a
                  href={`${getConfirmationUrl(leftSideLabel, rightSideLabel)}address/${wallet}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block sm:inline break-all whitespace-normal text-blue-500"
                  style={{ wordBreak: 'break-word', maxWidth: '42ch' }}
                  data-testid={`link-transaction-detail-wallet-${index}`}
                >
                  {truncateMiddle(wallet, 66)}
                </a>
              </span>
            </div>
            <div
              className="flex flex-col font-bold sm:flex-row justify-between py-1"
              data-testid={`container-transaction-detail-pay-${index}`}
            >
              <strong>{t('transactionRows.details.youPay')}</strong>{' '}
              <span className="font-normal">
                {leftSideValue} {leftSideLabel}
              </span>
            </div>
            <div
              className="flex flex-col font-bold sm:flex-row justify-between py-1"
              data-testid={`container-transaction-detail-receive-${index}`}
            >
              <strong>{t('transactionRows.details.youReceive')}</strong>{' '}
              <span className="font-normal">
                {rightSideValue} {rightSideLabel}
              </span>
            </div>
            <div
              className="flex flex-col font-bold sm:flex-row justify-between py-1"
              data-testid={`container-transaction-detail-network-${index}`}
            >
              <strong>{t('transactionRows.details.network')}</strong>
              <span className="font-normal">
                {getNetwork(leftSideLabel, rightSideLabel, t)}
              </span>
            </div>
            <div
              className="flex flex-col font-bold sm:flex-row justify-between py-1"
              data-testid={`container-transaction-detail-status-${index}`}
            >
              <strong>{t('transactionRows.details.status')}</strong>
              <span className="font-normal">
                {getStatusText(status)}
                <span>
                  <a
                    href={`https://app.paytrie.com/Status?id=${transactionId}`}
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                    target="_blank"
                    data-testid={`link-transaction-detail-status-${index}`}
                  >
                    <ExternalLinkIcon className="inline-block w-5 h-5" />
                  </a>
                </span>
              </span>
            </div>
            <div
              className="flex flex-col sm:flex-row justify-between py-1"
              data-testid={`container-transaction-detail-tx-${index}`}
            >
              <strong>{t('transactionRows.details.txLink')}</strong>
              <span>
                <a
                  href={`${getConfirmationUrl(leftSideLabel, rightSideLabel)}tx/${paymentId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block sm:inline break-all whitespace-normal text-blue-500"
                  style={{ wordBreak: 'break-word', maxWidth: '42ch' }}
                  data-testid={`link-transaction-detail-tx-${index}`}
                >
                  {truncateMiddle(paymentId, 66)}
                </a>
              </span>
            </div>
            {showCancelButton && (
              <div
                className="flex justify-start mb-0"
                data-testid={`container-transaction-detail-cancel-${index}`}
              >
                <button
                  className="bg-black border dark:border-black dark:bg-white dark:text-black text-white py-2 px-4 mt-3"
                  onClick={() => setIsCancelConfirmVisible(true)}
                  data-testid={`btn-transaction-detail-cancel-${index}`}
                >
                  {t('transactionRows.cancelButton')}
                </button>
              </div>
            )}
          </div>
        </td>
      </tr>

      {isCancelConfirmVisible && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          data-testid="modal-transaction-cancel-overlay"
        >
          <div className="absolute inset-0 bg-black opacity-70 z-40"></div>
          <div
            className="relative bg-white dark:bg-pebble p-6 shadow-lg text-center z-50 flex flex-col"
            style={{ width: '400px', height: '200px' }}
            data-testid="container-transaction-cancel-modal"
          >
            <h2
              className="text-lg font-normal mb-4 mt-8"
              data-testid="text-transaction-cancel-title"
            >
              {t('transactionRows.cancelModal.title')}
            </h2>
            <div
              className="absolute bottom-4 left-0 right-0 flex justify-center space-x-6 mb-5"
              data-testid="container-transaction-cancel-buttons"
            >
              <button
                className="bg-black dark:bg-white text-white dark:text-black py-1 px-6"
                onClick={handleCancelTransaction}
                data-testid="btn-transaction-cancel-confirm"
              >
                {t('transactionRows.cancelModal.yes')}
              </button>
              <button
                className="bg-white dark:bg-pebble text-black dark:text-white py-1 px-6 border border-black dark:border-white"
                onClick={() => setIsCancelConfirmVisible(false)}
                data-testid="btn-transaction-cancel-reject"
              >
                {t('transactionRows.cancelModal.no')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function showTXNDetails(index: number) {
  const panel = document.getElementById(`panel${index}`)
  panel?.classList.toggle('hidden')
}

export default TransactionRow

/*
POTENTIAL IMPROVEMENTS:
1. Replace direct DOM manipulation with React state for showing/hiding details
2. Add proper animation for expanding/collapsing details
3. Implement proper error handling for cancelation with user feedback
4. Add loading state for cancel button during API request
5. Add confirmation toast messages after successful cancellation
6. Implement proper focus management for accessibility when details expand
7. Add keyboard navigation for order items and their details
8. Consider using a modal component library instead of custom modals
9. Add proper aria attributes for accessibility
10. Implement better mobile responsiveness for details view
11. Add retry mechanism for failed cancel actions
12. Consider adding pagination for large transaction lists
13. Add sorting options for transaction items
14. Implement filtering by transaction status
15. Consider adding search functionality for transaction ID or wallet
*/
